import type { AircraftTypeInfo, AirlineInfo } from '@flightscanner/shared';

/**
 * Loads bundled reference data (built by scripts/build-reference-data.ts) and
 * provides lookup helpers for airline (from callsign prefix), aircraft type, and
 * a rough origin country inferred from registration prefix or airline country.
 */

let airlinesByIcao: Map<string, AirlineInfo> | null = null;
let typesByIcao: Map<string, AircraftTypeInfo> | null = null;
let loadPromise: Promise<void> | null = null;

/** Curated registration-prefix -> country map for the common prefixes. */
const REG_PREFIX_COUNTRY: Array<{ prefix: string; country: string }> = [
  { prefix: 'N', country: 'United States' },
  { prefix: 'A', country: 'United States' },
  { prefix: 'C', country: 'Canada' },
  { prefix: 'G', country: 'United Kingdom' },
  { prefix: 'D', country: 'Germany' },
  { prefix: 'F', country: 'France' },
  { prefix: 'EI', country: 'Ireland' },
  { prefix: 'EC', country: 'Ireland' },
  { prefix: 'PH', country: 'Netherlands' },
  { prefix: 'OO', country: 'Belgium' },
  { prefix: 'SE', country: 'Sweden' },
  { prefix: 'LN', country: 'Norway' },
  { prefix: 'OY', country: 'Denmark' },
  { prefix: 'OH', country: 'Finland' },
  { prefix: 'HB', country: 'Switzerland' },
  { prefix: 'OE', country: 'Austria' },
  { prefix: 'I', country: 'Italy' },
  { prefix: 'EC', country: 'Spain' },
  { prefix: 'EC-', country: 'Spain' },
  { prefix: 'VP', country: 'Argentina' },
  { prefix: 'LV', country: 'Argentina' },
  { prefix: 'PR', country: 'Brazil' },
  { prefix: 'PT', country: 'Brazil' },
  { prefix: 'PP', country: 'Brazil' },
  { prefix: 'CU', country: 'Cuba' },
  { prefix: 'B', country: 'China' },
  { prefix: 'JA', country: 'Japan' },
  { prefix: 'HL', country: 'South Korea' },
  { prefix: 'RP', country: 'Philippines' },
  { prefix: 'HS', country: 'Thailand' },
  { prefix: '9V', country: 'Singapore' },
  { prefix: 'VH', country: 'Australia' },
  { prefix: 'ZK', country: 'New Zealand' },
  { prefix: 'ZL', country: 'Australia' },
  { prefix: 'VX', country: 'India' },
  { prefix: 'UR', country: 'Russia' },
  { prefix: 'RA', country: 'Russia' },
  { prefix: '4X', country: 'Israel' },
  { prefix: '4L', country: 'Georgia' },
  { prefix: 'TC', country: 'Turkey' },
  { prefix: 'SU', country: 'Egypt' },
  { prefix: 'A6', country: 'United Arab Emirates' },
  { prefix: 'A7', country: 'Qatar' },
  { prefix: 'HZ', country: 'Saudi Arabia' },
  { prefix: 'EP', country: 'Iran' },
  { prefix: '5N', country: 'Nigeria' },
  { prefix: 'ZS', country: 'South Africa' },
];

export async function loadReference(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
  const [airlinesRes, typesRes] = await Promise.all([
    fetch('/data/airlines.json'),
    fetch('/data/aircraft-types.json'),
  ]);
  const airlines: AirlineInfo[] = airlinesRes.ok ? await airlinesRes.json() : [];
  const types: AircraftTypeInfo[] = typesRes.ok ? await typesRes.json() : [];
  airlinesByIcao = new Map(airlines.map((a) => [a.icao, a]));
  typesByIcao = new Map(types.map((t) => [t.icao, t]));
  })();
  return loadPromise;
}

export function getAirlineByIcao(icao: string | null | undefined): AirlineInfo | null {
  if (!icao || !airlinesByIcao) return null;
  return airlinesByIcao.get(icao.toUpperCase()) ?? null;
}

export function getAllAirlines(): AirlineInfo[] {
  return airlinesByIcao ? [...airlinesByIcao.values()] : [];
}

export function getAllTypes(): AircraftTypeInfo[] {
  return typesByIcao ? [...typesByIcao.values()] : [];
}

export function getAllCountries(): string[] {
  if (!airlinesByIcao) return [];
  const set = new Set<string>();
  for (const a of airlinesByIcao.values()) if (a.country) set.add(a.country);
  return [...set].sort();
}

/** Resolve an airline from a flight callsign (e.g. "UAL123" -> United). */
export function airlineFromCallsign(callsign: string | null | undefined): AirlineInfo | null {
  if (!callsign) return null;
  const prefix = callsign.replace(/[^A-Z]/g, '').slice(0, 3).toUpperCase();
  return getAirlineByIcao(prefix);
}

export function getTypeInfo(typeCode: string | null | undefined): AircraftTypeInfo | null {
  if (!typeCode || !typesByIcao) return null;
  return typesByIcao.get(typeCode.toUpperCase()) ?? null;
}

export function inferCountry(registration: string | null, airline: AirlineInfo | null): string | null {
  if (airline?.country) return airline.country;
  if (!registration) return null;
  const reg = registration.toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Longest matching prefix wins.
  let best: string | null = null;
  for (const { prefix, country } of REG_PREFIX_COUNTRY) {
    if (reg.startsWith(prefix) && (!best || prefix.length > best.length)) best = country;
  }
  return best;
}

export function isMilitaryByReference(
  typeCode: string | null,
  airline: AirlineInfo | null,
): boolean {
  if (airline?.isMilitary) return true;
  const t = getTypeInfo(typeCode);
  return Boolean(t?.isMilitary);
}
